import React from "react";
import { Label } from "~/components/ui/label";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { FieldComponentProps, isFieldApi } from "~/lib/form";
import { cn } from "~/shared/lib/utils";

interface RadioOption {
  value: string;
  label: string;
  description?: string;
}

interface ValidatedRadioGroupProps extends FieldComponentProps {
  options: RadioOption[];
  orientation?: "horizontal" | "vertical";
  disabled?: boolean;
}

export const ValidatedRadioGroup: React.FC<ValidatedRadioGroupProps> = (props) => {
  const { field, label, className, options, orientation = "vertical", disabled } = props;
  const groupId = `${field.name}-${React.useId()}-radio`;

  if (!isFieldApi(field)) {
    return null;
  }

  const meta = field.state.meta;

  return (
    <div className={cn("space-y-2", className)}>
      <Label>{label}</Label>
      <RadioGroup
        value={String(field.state.value ?? "")}
        onValueChange={(value) => field.handleChange(value)}
        onBlur={field.handleBlur}
        disabled={field.form.state.isSubmitting || disabled}
        className={orientation === "horizontal" ? "flex flex-wrap gap-3" : "space-y-3"}
        aria-invalid={!!meta.errors.length}
        aria-describedby={meta.errors.length ? `${groupId}-errors` : undefined}
      >
        {options.map((option) => (
          <div
            key={option.value}
            className={cn(
              "flex items-start gap-3 rounded-md border p-3",
              orientation === "horizontal" ? "min-w-[180px]" : "",
            )}
          >
            <RadioGroupItem id={`${groupId}-${option.value}`} value={option.value} />
            <div className="space-y-1">
              <Label className="cursor-pointer" htmlFor={`${groupId}-${option.value}`}>
                {option.label}
              </Label>
              {option.description ? (
                <p className="text-muted-foreground text-sm">{option.description}</p>
              ) : null}
            </div>
          </div>
        ))}
      </RadioGroup>
      {meta.isTouched && meta.errors.length > 0 ? (
        <div
          id={`${groupId}-errors`}
          role="alert"
          aria-live="polite"
          className="text-destructive text-sm font-medium"
        >
          {label}: {meta.errors.join(", ")}
        </div>
      ) : null}
    </div>
  );
};
